/**
 * German catalogue — the source of truth for the message shape.
 *
 * Keys are English (like the rest of the code); only the values are localised.
 * `en.ts` is typed against `Messages`, so adding a key here without adding it
 * there is a compile error rather than a string that silently stays German.
 *
 * Placeholders are `{name}` and are filled by `t(key, { name })`.
 */
export const de = {
  common: {
    /** Product name — identical in every language, by design. */
    appName: 'Insurensa',
    adminTitle: 'Insurensa Verwaltung',
    portalTitle: 'Insurensa Partnerportal',
    signOut: 'Ausloggen',
    back: 'Zurück',
    cancel: 'Abbrechen',
    save: 'Speichern',
    saving: 'Wird gespeichert…',
    open: 'Öffnen',
    details: 'Details',
    view: 'Ansehen',
    all: 'Alle',
    yes: 'Ja',
    no: 'Nein',
    status: 'Status',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    loading: 'Wird geladen…',
    unknownError: 'Unbekannter Fehler',
    reference: 'Referenz',
    referenceOf: 'Referenz {reference}',
    done: 'Erledigt',
    openCount: '{count} offen',
    copyright: 'Copyright © Insurensa {year}',
    choose: 'Bitte wählen',
    activate: 'Aktivieren',
    deactivate: 'Deaktivieren',
    approve: 'Freigeben',
    decline: 'Ablehnen',
    reason: 'Begründung',
    note: 'Notiz',
    date: 'Datum',
    createdAt: 'Eingegangen',
    kilometres: '{km} km',
    inLast30Days: '+{count} in 30 Tagen',
  },

  language: {
    label: 'Sprache',
    switchTo: 'Sprache wechseln',
  },

  /** The data grid, shared by both consoles. */
  table: {
    copy: 'Kopieren',
    csv: 'CSV',
    excel: 'Excel',
    pdf: 'PDF',
    print: 'Drucken',
    search: 'Suchen:',
    entries: 'Einträge',
    noEntries: 'Keine Einträge vorhanden',
    noData: 'Keine Daten vorhanden',
    showing: 'Zeige {first} bis {last} von {total} Einträgen',
    previous: 'Zurück',
    next: 'Weiter',
    pageOf: 'Seite {page} von {total}',
  },

  /** Form labels shared across registration, profile and team screens. */
  fields: {
    firstName: 'Vorname',
    lastName: 'Nachname',
    name: 'Name',
    companyName: 'Firmenname',
    company: 'Firma',
    companyNameOptional: 'Firmenname (optional)',
    contactPerson: 'Ansprechpartner',
    email: 'E-Mail',
    emailAddress: 'E-Mail-Adresse',
    contactEmail: 'Kontakt-E-Mail',
    loginEmail: 'Anmelde-E-Mail',
    phone: 'Telefon',
    alternatePhone: 'Weitere Telefonnummer',
    username: 'Benutzername',
    password: 'Passwort',
    currentPassword: 'Aktuelles Passwort',
    newPassword: 'Neues Passwort',
    confirmPassword: 'Passwort bestätigen',
    confirmNewPassword: 'Neues Passwort bestätigen',
    role: 'Rolle',
    registrationNumber: 'Registrierungsnummer',
    registrationNumberShort: 'Registrierungsnr.',
    streetNumber: 'Straße und Hausnummer',
    postalCode: 'Postleitzahl',
    postalCodeShort: 'PLZ',
    address: 'Adresse',
    fullAddress: 'Vollständige Anschrift',
    city: 'Ort',
    state: 'Bundesland',
    district: 'Landkreis',
    coordinates: 'Koordinaten',
    radius: 'Umkreis',
    distance: 'Entfernung',
    amount: 'Beitrag',
    amountEur: 'Beitrag (EUR)',
    paymentFrequency: 'Zahlweise',
    title: 'Titel',
    description: 'Beschreibung',
    insuranceLine: 'Sparte',
    user: 'Nutzer',
    partner: 'Partner',
    offers: 'Angebote',
    leads: 'Anfragen',
    reviews: 'Bewertungen',
    registeredAt: 'Registriert',
    lastActive: 'Zuletzt aktiv',
    lastSignIn: 'Letzte Anmeldung',
    verified: 'Bestätigt',
    sentAt: 'Gesendet',
    contact: 'Kontakt',
  },

  validation: {
    registrationFormat: 'Format: A-BBBB-CCCCC-DD',
    passwordsDoNotMatch: 'Passwörter stimmen nicht überein',
    passwordMinChars: 'Mindestens {min} Zeichen',
    passwordRule: 'Mindestens {min} Zeichen, Groß- und Kleinbuchstaben, eine Ziffer.',
    passwordRuleShort: 'Mind. {min} Zeichen, Groß-/Kleinbuchstaben, Ziffer.',
    noCitiesFound: 'Keine Orte gefunden',
  },

  admin: {
    nav: {
      dashboard: 'Dashboard',
      partners: 'Partner',
      leads: 'Anfragen',
      users: 'Nutzer',
      reviews: 'Bewertungen',
      appReleases: 'App-Versionen',
      team: 'Team',
      auditLog: 'Protokoll',
    },

    login: {
      title: 'Insurensa Verwaltung',
      subtitle: 'Bitte melden Sie sich mit Ihrem Konto an.',
      usernamePlaceholder: 'Benutzername hier...',
      passwordPlaceholder: 'Passwort hier...',
      submit: 'Am Dashboard anmelden',
      submitting: 'Anmeldung läuft…',
    },

    dashboard: {
      subheading: 'Überblick über Marktplatz und offene Aufgaben',
      users: 'Nutzer',
      leads: 'Anfragen',
      offers: 'Angebote',
      activePartners: 'Aktive Partner',
      accepted: '{percent}% angenommen',
      awaitingReview: '{count} warten auf Prüfung',
      leadsByLine: 'Anfragen nach Sparte',
      openTasks: 'Offene Aufgaben',
      reviewRegistrations: 'Partnerregistrierungen prüfen',
      moderateReviews: 'Bewertungen moderieren',
    },

    partners: {
      subheading: 'Registrierungen prüfen und bestehende Partner verwalten',
      empty: 'Keine Partner gefunden',
      exportTitle: 'Partner',
    },

    partnerDetail: {
      masterData: 'Stammdaten',
      noCoordinatesWarning:
        'Ohne Koordinaten kann dieser Partner keine Anfragen über die Umkreissuche erhalten.',
      coordinatesMissing: 'Nicht hinterlegt',
      documents: 'Dokumente',
      noDocuments: 'Keine Dokumente hinterlegt.',
      documentRegistration: 'Registrierung',
      documentApproval: 'Freigabe',
      decisionHistory: 'Entscheidungsverlauf',
      noDecision: 'Noch keine Entscheidung getroffen.',
      decision: 'Entscheidung',
      handledBy: 'Bearbeiter',
      activity: 'Aktivität',
      assignedLeads: 'Zugeordnete Anfragen',
      sentOffers: 'Abgegebene Angebote',
      reasonPlaceholder: 'Bei Ablehnung erforderlich',
      reasonHint: 'Die Begründung wird dem Partner per E-Mail mitgeteilt.',
      approvedNote:
        'Nach der Freigabe werden bereits eingegangene Anfragen im Umkreis automatisch zugeordnet.',
      approvedFeedback: 'Partner freigegeben. {count} bestehende Anfrage(n) wurden zugeordnet.',
      declinedFeedback: 'Partner abgelehnt.',
    },

    leads: {
      subheading: 'Alle eingegangenen Versicherungsanfragen',
      empty: 'Keine Anfragen gefunden',
      exportTitle: 'Anfragen',
      exportTitleFiltered: 'Anfragen – {line}',
      guest: 'Gast',
    },

    leadDetail: {
      heading: 'Anfrage',
      customerAnswers: 'Angaben des Kunden',
      noAnswers: 'Keine Angaben vorhanden.',
      uploadedDocuments: 'Hochgeladene Dokumente',
      offersWithCount: 'Angebote ({count})',
      noOffers: 'Noch keine Angebote',
      overview: 'Übersicht',
      requestedRadius: 'Gewünschter Umkreis',
      phoneAfterAcceptance: 'Erst nach Annahme',
      assignedPartners: 'Zugeordnete Partner ({count})',
      noAssignedPartners:
        'Keine Partner im Umkreis. Prüfen Sie, ob Partner mit Koordinaten hinterlegt sind.',
      viewed: 'Angesehen',
      notViewed: 'Noch nicht angesehen',
    },

    users: {
      subheading: 'Kunden der App',
      empty: 'Keine Nutzer gefunden',
      exportTitle: 'Nutzer',
      guest: 'Gast',
      guestNoAccount: 'Gast (ohne Anmeldung)',
    },

    userDetail: {
      guestSubheading: 'Gastzugang, ohne Anmeldung',
      accountSubheading: 'Nutzerkonto',
      leadsWithCount: 'Anfragen ({count})',
      noLeads: 'Keine Anfragen',
      account: 'Konto',
      deleteAccount: 'Konto löschen',
      deleteExplanation:
        'Das Konto wird anonymisiert und kann sich nicht mehr anmelden. Anfragen und Angebote bleiben für die Nachvollziehbarkeit erhalten.',
      deleteConfirm: 'Endgültig löschen',
    },

    reviews: {
      subheading: 'Kundenbewertungen prüfen, bevor sie öffentlich sichtbar werden',
      filterLabel: 'Status filtern',
      emptyForStatus: 'In diesem Status liegen keine Bewertungen vor.',
      moderatedBy: 'geprüft von {name}',
      attachment: 'Anhang {index}',
      notePrefix: 'Notiz: {note}',
      reasonPlaceholder: 'Begründung (optional)',
    },

    appReleases: {
      heading: 'App-Versionen',
      subheading: 'Steuert Update-Hinweise in der mobilen App',
      published: 'Veröffentlichte Versionen',
      platform: 'Plattform',
      version: 'Version',
      minimumVersion: 'Mindestversion',
      createdAt: 'Erstellt',
      forced: 'Erzwungen',
      empty: 'Keine Versionen hinterlegt',
      addVersion: 'Version hinzufügen',
      minimumVersionHint: 'Ältere Installationen werden zum Update gezwungen.',
      releaseNotes: 'Release Notes',
      showUpdatePrompt: 'Update-Hinweis anzeigen',
      forceUpdate: 'Update erzwingen',
      submit: 'Version anlegen',
    },

    team: {
      subheading: 'Zugänge zur Verwaltung',
      members: 'Mitarbeitende',
      disabled: 'Deaktiviert',
      yourself: 'Sie selbst',
      createAccess: 'Zugang anlegen',
      initialPassword: 'Initiales Passwort',
      initialPasswordHint:
        'Mindestens {min} Zeichen. Muss bei der ersten Anmeldung geändert werden.',
      creating: 'Wird angelegt…',
    },

    auditLog: {
      heading: 'Protokoll',
      subheading: 'Nachvollziehbare Aufzeichnung aller Änderungen',
      timestamp: 'Zeitpunkt',
      action: 'Aktion',
      entity: 'Objekt',
      performedBy: 'Ausgeführt von',
      ip: 'IP',
      empty: 'Keine Einträge',
      entityPartner: 'Partner',
      entityUser: 'Nutzer',
      entityReview: 'Bewertung',
      entityOffer: 'Angebot',
      entityAdmin: 'Team',
      entityAppRelease: 'App-Version',
    },
  },

  partner: {
    nav: {
      dashboard: 'Dashboard',
      leads: 'Übersicht Leads',
      offers: 'Meine Angebote',
      reviews: 'Meine Bewertungen',
      profile: 'Profil',
    },

    access: {
      title: 'Zugangscode',
      subtitle: 'Bitte geben Sie den Zugangscode ein, um zum Partnerportal zu gelangen.',
      digitLabel: 'Ziffer {index}',
      submit: 'Weiter',
      submitting: 'Wird geprüft…',
      help: 'Den Code erhalten Sie von Insurensa. Bei Fragen wenden Sie sich an Ihren Ansprechpartner.',
    },

    login: {
      title: 'Willkommen bei Insurensa',
      subtitle: 'Bitte melden Sie sich mit Ihrem Konto an.',
      resetNotice: 'Aus Sicherheitsgründen wurde Ihr Passwort zurückgesetzt.',
      requestNewPassword: 'Neues Passwort anfordern',
      submit: 'Am Dashboard anmelden',
      submitting: 'Anmeldung läuft…',
      forgotPassword: 'Passwort vergessen?',
      noAccount: 'Noch kein Partnerkonto?',
      register: 'Registrieren',
    },

    register: {
      title: 'Als Partner registrieren',
      subtitle: 'Nach der Prüfung erhalten Sie Anfragen aus Ihrem Umkreis.',
      sectionContact: 'Ansprechpartner',
      sectionReach: 'Kontakt',
      sectionAddress: 'Anschrift',
      sectionCredentials: 'Zugangsdaten',
      sectionDocuments: 'Nachweise',
      registrationNumberHint: 'Ihre Vermittlerregisternummer.',
      phoneHint: 'Im Format +49…',
      cityHint: 'Bestimmt, aus welchem Umkreis Sie Anfragen erhalten.',
      documentsLabel: 'Nachweise (PDF)',
      documentsHint:
        'Gewerbeanmeldung, Registerauszug und Erlaubnis nach §34d GewO. Maximal 10 MB je Datei.',
      filesSelected: '{count} Datei(en) ausgewählt: {names}',
      acceptTerms: 'Ich akzeptiere die Nutzungsbedingungen und die Datenschutzerklärung.',
      alreadyRegistered: 'Bereits registriert?',
      submit: 'Registrierung absenden',
      submitting: 'Wird gesendet…',
      doneTitle: 'Registrierung eingegangen',
      doneMessage:
        'Vielen Dank. Wir prüfen Ihre Unterlagen und melden uns per E-Mail. Nach der Freigabe können Sie sich mit Ihrer Registrierungsnummer anmelden.',
      toSignIn: 'Zur Anmeldung',
    },

    forgotPassword: {
      title: 'Passwort zurücksetzen',
      subtitle: 'Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.',
      sent: 'Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir eine E-Mail versendet. Der Link ist 60 Minuten gültig.',
      submit: 'Link anfordern',
      submitting: 'Wird gesendet…',
      backToSignIn: 'Zurück zur Anmeldung',
    },

    resetPassword: {
      title: 'Neues Passwort festlegen',
      invalidLink: 'Der Link ist unvollständig oder abgelaufen.',
      requestNewLink: 'Neuen Link anfordern',
      done: 'Ihr Passwort wurde geändert. Alle anderen Sitzungen wurden beendet.',
      toSignIn: 'Zur Anmeldung',
      submit: 'Passwort speichern',
    },

    dashboard: {
      subheading: 'Ihre Anfragen und Angebote auf einen Blick',
      openLeads: 'Offene Anfragen',
      noOfferYet: 'Noch kein Angebot',
      totalLeads: 'Anfragen gesamt',
      offers: 'Angebote',
      offersAccepted: '{count} angenommen',
      conversionRate: 'Abschlussquote',
      awaitingOfferAlert: 'Sie haben {count} Anfrage(n) ohne Angebot.',
      handleNow: 'Jetzt bearbeiten',
      leadsByLine: 'Anfragen nach Sparte',
      viewAll: 'Alle ansehen',
      yourRating: 'Ihre Bewertung',
      ratingBasis: 'Basierend auf {count} veröffentlichten Bewertungen.',
      noRatings:
        'Noch keine veröffentlichten Bewertungen. Kunden können Sie bewerten, nachdem sie Ihr Angebot angenommen haben.',
    },

    leads: {
      heading: 'Übersicht Leads',
      subheading: 'Anfragen aus Ihrem Umkreis',
      new: 'Neu',
      myOffer: 'Mein Angebot',
      noOffer: 'Kein Angebot',
      acceptedWithAmount: 'Angenommen · {amount}',
      onlyWithoutOffer: 'Nur ohne Angebot',
      empty: 'Sobald eine passende Anfrage in Ihrem Umkreis eingeht, erscheint sie hier.',
      exportTitle: 'Anfragen',
    },

    leadDetail: {
      heading: 'Anfrage',
      backToOverview: 'Zurück zur Übersicht',
      customerAnswers: 'Angaben des Kunden',
      noAnswers: 'Keine Angaben vorhanden.',
      customerUploads: 'Vom Kunden hochgeladen',
      editOffer: 'Angebot bearbeiten',
      makeOffer: 'Angebot abgeben',
      lockedNotice:
        'Ihr Angebot über {amount} wurde angenommen. Es kann nicht mehr geändert werden.',
      sentNotice: 'Angebot wurde gesendet. Der Kunde wird benachrichtigt.',
      amountPlaceholder: '49,90',
      titlePlaceholder: 'z. B. Vollkasko mit 300 EUR SB',
      bodyPlaceholder: 'Leistungen, Besonderheiten und Hinweise zum Angebot',
      bodyCounter: '{count} / 4000 Zeichen',
      documentLabel: 'Angebotsdokument (PDF, optional)',
      submitNew: 'Angebot senden',
      submitUpdate: 'Angebot aktualisieren',
      submitting: 'Wird gesendet…',
      updateNotice: 'Eine Änderung markiert das Angebot für den Kunden wieder als ungelesen.',
      contact: 'Kontakt',
      customerPhone: 'Telefonnummer des Kunden',
      contactLocked:
        'Die Kontaktdaten des Kunden werden freigeschaltet, sobald Ihr Angebot angenommen wurde.',
      request: 'Anfrage',
      yourOffer: 'Ihr Angebot',
    },

    offers: {
      heading: 'Meine Angebote',
      subheading: 'Alle von Ihnen abgegebenen Angebote',
      offer: 'Angebot',
      seen: 'Gelesen',
      notSeen: 'Noch nicht',
      empty: 'Öffnen Sie eine Anfrage, um Ihr erstes Angebot abzugeben.',
      exportTitle: 'Meine Angebote',
    },

    reviews: {
      heading: 'Meine Bewertungen',
      subheading: 'Was Kunden über Sie schreiben',
      publishedCount: '{count} veröffentlichte Bewertungen',
      none: 'Noch keine veröffentlichten Bewertungen. Kunden können Sie bewerten, nachdem sie Ihr Angebot angenommen haben. Bewertungen werden vor der Veröffentlichung geprüft.',
      empty: 'Keine Bewertungen',
    },

    profile: {
      heading: 'Profil',
      approved: 'Freigegeben',
      inReview: 'In Prüfung',
      masterData: 'Stammdaten und Einzugsgebiet',
      cityHint: 'Legt den Mittelpunkt Ihres Einzugsgebiets neu fest.',
      radiusLabel: 'Einzugsgebiet: {km} km',
      radiusHint:
        'Sie erhalten Anfragen von Kunden innerhalb dieses Umkreises. Nach dem Speichern werden passende bestehende Anfragen nachträglich zugeordnet.',
      saved: 'Profil gespeichert.',
      savedWithMatches:
        'Profil gespeichert. {count} weitere Anfrage(n) wurden Ihnen zugeordnet.',
      changePassword: 'Passwort ändern',
      changingPassword: 'Wird geändert…',
      passwordChanged: 'Passwort geändert. Alle Sitzungen wurden beendet — Sie werden abgemeldet.',
      account: 'Konto',
      partnerSince: 'Partner seit',
      rating: 'Bewertung',
      noRating: 'Noch keine',
      attention: 'Achtung',
      noCoordinatesWarning:
        'Für Ihr Konto sind keine Koordinaten hinterlegt, daher erhalten Sie derzeit keine Anfragen. Wählen Sie links eine Postleitzahl und einen Ort aus und speichern Sie.',
    },
  },
  /**
   * Public site (the insurensa-web project).
   *
   * Consumer-first: the customer story leads, the broker path follows. The
   * copy leans on what the platform actually guarantees rather than on claims
   * nobody can check — notably that a customer's phone number is withheld
   * until they accept an offer, which is enforced in the API, not marketing.
   */
  marketing: {
    nav: {
      skipToContent: 'Zum Inhalt springen',
      howItWorks: 'So funktioniert es',
      lines: 'Sparten',
      forBrokers: 'Für Makler',
      partnerLogin: 'Partner-Login',
      openMenu: 'Menü öffnen',
    },

    hero: {
      eyebrow: 'Versicherungsangebote aus Ihrer Region',
      title: 'Ein Anliegen beschreiben. Angebote von Maklern aus Ihrer Nähe erhalten.',
      body: 'Sagen Sie einmal, worum es geht. Geprüfte Maklerinnen und Makler aus Ihrem Umkreis melden sich mit einem konkreten Angebot. Sie entscheiden in Ruhe, mit wem Sie sprechen möchten.',
      appStore: 'Im App Store laden',
      playStore: 'Bei Google Play laden',
      storesPending: 'Die Store-Links folgen zur Veröffentlichung der App.',
      brokerLink: 'Sie sind Maklerin oder Makler?',
    },

    steps: {
      title: 'In drei Schritten zum Angebot',
      lead: 'Kein Vergleichsrechner, keine Callcenter-Schleife — Menschen aus Ihrer Region, die Ihnen ein Angebot schreiben.',
      oneTitle: 'Anfrage stellen',
      oneBody: 'Ein paar Fragen zu Ihrem Anliegen, in der App beantwortet. Für ein Angebot reicht das aus — ein Konto brauchen Sie dafür nicht.',
      twoTitle: 'Angebote erhalten',
      twoBody: 'Ihre Anfrage geht an Makler im gewünschten Umkreis. Sie antworten mit Beitrag, Zahlweise und den Leistungen im Detail.',
      threeTitle: 'Auswählen',
      threeBody: 'Sie vergleichen die Angebote nebeneinander und nehmen eines an. Erst dann erhält dieser Makler Ihre Kontaktdaten.',
    },

    lines: {
      title: 'Diese Sparten deckt Insurensa ab',
      lead: 'Von der Kfz-Versicherung bis zum Rechtsschutz — jeweils mit den Fragen, die für eine belastbare Empfehlung nötig sind.',
    },

    trust: {
      title: 'Warum Insurensa',
      privacyTitle: 'Ihre Nummer bleibt Ihre Nummer',
      privacyBody: 'Ihre Telefonnummer wird erst freigegeben, wenn Sie ein Angebot annehmen. Bis dahin sieht sie kein Makler — das ist in der Plattform so gebaut, nicht bloß versprochen.',
      localTitle: 'Makler aus Ihrer Region',
      localBody: 'Angebote kommen von Maklerinnen und Maklern in Ihrer Nähe, die ihr Einzugsgebiet selbst festlegen. Wer Sie berät, sitzt in erreichbarer Entfernung.',
      freeTitle: 'Kostenlos und unverbindlich',
      freeBody: 'Für Sie entstehen keine Kosten, und aus einer Anfrage folgt kein Vertrag. Sie können jedes Angebot liegen lassen.',
      checkedTitle: 'Geprüfte Partner und Bewertungen',
      checkedBody: 'Jeder Partner wird vor der Freischaltung anhand seiner Unterlagen geprüft. Bewertungen werden vor der Veröffentlichung moderiert.',
    },

    brokers: {
      eyebrow: 'Für Maklerinnen und Makler',
      title: 'Anfragen aus Ihrem Einzugsgebiet, ohne Leadkauf',
      body: 'Sie legen fest, aus welchem Umkreis Sie Anfragen erhalten möchten. Passende Anfragen erscheinen in Ihrem Portal, Sie geben ein Angebot ab, und der Kunde entscheidet.',
      pointRadius: 'Umkreis frei wählbar — von der Nachbarschaft bis zum ganzen Landkreis.',
      pointFair: 'Alle Angebote zu einer Anfrage stehen nebeneinander. Es gibt keine bezahlte Platzierung.',
      pointContact: 'Kontaktdaten erhalten Sie, sobald Ihr Angebot angenommen wurde.',
      cta: 'Als Partner registrieren',
      login: 'Zum Partner-Login',
      note: 'Für die Freischaltung benötigen wir Gewerbeanmeldung, Registerauszug und die Erlaubnis nach §34d GewO.',
    },

    legal: {
      pendingTitle: 'Inhalt noch nicht hinterlegt',
      pendingBody: 'Diese Seite ist angelegt, aber noch nicht mit Inhalt gefüllt. Für den öffentlichen Betrieb in Deutschland ist ein vollständiges Impressum nach §5 TMG sowie eine Datenschutzerklärung nach DSGVO erforderlich — beides muss von Insurensa bereitgestellt und rechtlich geprüft werden.',
      backHome: 'Zurück zur Startseite',
    },

    footer: {
      tagline: 'Versicherungsangebote von Maklern aus Ihrer Region.',
      product: 'Produkt',
      legal: 'Rechtliches',
      imprint: 'Impressum',
      privacy: 'Datenschutz',
      terms: 'Nutzungsbedingungen',
      partnerPortal: 'Partnerportal',
      rights: 'Alle Rechte vorbehalten.',
    },
  },
};

/** Shape every catalogue must satisfy. */
export type Messages = typeof de;
